// src/Hooks/useImportExcelState.js

import { useState, useRef } from 'react';

const normalizeString = (str) => {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/[\s,]+/g, '')
    .toLowerCase();
};

export const useImportExcelState = () => {
  const fileInputRef = useRef(null);
  const cancelProcessRef = useRef(false);
  const isTemplateFromReinstateRef = useRef(false);

  // --- Core UI & Data State ---
  const [viewMode, setViewMode] = useState('MENU_VIEW');
  const [parsedData, setParsedData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [currentFilename, setCurrentFilename] = useState('');
  const [pendingNextColumn, setPendingNextColumn] = useState(null);
  const [userZoomLevel, setUserZoomLevel] = useState(null);

  // --- History & Session State ---
  const [importHistory, setImportHistory] = useState([]);
  const [selectedHistoryIds, setSelectedHistoryIds] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [successfullyImportedIds, setSuccessfullyImportedIds] = useState([]);
  const [sessionBatches, setSessionBatches] = useState({});

  // --- Processing & Flow State ---
  const [isPhotoFlowActive, setIsPhotoFlowActive] = useState(false);
  const [pendingMissingColsCheck, setPendingMissingColsCheck] = useState(false);
  const [stagedFiles, setStagedFiles] = useState([]);
  const [manualMatchRowId, setManualMatchRowId] = useState(null);
  const [resolveCoordsState, setResolveCoordsState] = useState({
    open: false,
    rowId: null,
    searchTarget: '',
    actualTitle: '',
    geoSuffix: '',
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [isCancelling, setIsCancelling] = useState(false);

  // --- Deferred Deletion State ---
  const [pendingReinstateBatchIds, setPendingReinstateBatchIds] = useState([]);
  const [pendingDuplicateDeletions, setPendingDuplicateDeletions] = useState([]);
  const [pendingDocumentDeletions, setPendingDocumentDeletions] = useState([]);

  // --- Duplicate Check State ---
  const [checkedRightIds, setCheckedRightIds] = useState([]);
  const [triggerDuplicateDelete, setTriggerDuplicateDelete] = useState(false);

  // --- Dialog & Alert State ---
  const [invalidMatchState, setInvalidMatchState] = useState({
    open: false,
    rowId: null,
    colName: null,
    cellValue: '',
    type: null,
  });
  const [bulkMatchState, setBulkMatchState] = useState({
    open: false,
    rowId: null,
    colName: '',
    originalValue: '',
    newValue: '',
    type: '',
  });
  const [missingColumnsDialog, setMissingColumnsDialog] = useState({
    open: false,
    missingCols: [],
  });
  const [alertState, setAlertState] = useState({
    open: false,
    message: '',
    severity: 'info',
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: null,
    message: '',
    confirmText: 'Yes',
    cancelText: 'No',
    severity: 'warning',
    hideCancel: false,
  });
  const [showHistoryDeleteConfirm, setShowHistoryDeleteConfirm] = useState(false);
  const [showHistoryDeleteConfirm2, setShowHistoryDeleteConfirm2] = useState(false);
  const [showPublishConfirm1, setShowPublishConfirm1] = useState(false);
  const [showBatchFilenameDialog, setShowBatchFilenameDialog] = useState(false);

  // --- Mapping & Settings State ---
  const [categories, setCategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [globalCategory, setGlobalCategory] = useState('');
  const [customRows, setCustomRows] = useState([]);
  const [markerIcons, setMarkerIcons] = useState([]);
  const [makeUppercase, setMakeUppercase] = useState(false);
  const [rteFontType, setRteFontType] = useState('Helvetica');
  const [rteFontSize, setRteFontSize] = useState('10pt');
  const [imHeadingFontType, setImHeadingFontType] = useState('Helvetica');
  const [imHeadingFontSize, setImHeadingFontSize] = useState('12pt');
  const [selectedCustomIds, setSelectedCustomIds] = useState([]);
  const [rowMappings, setRowMappings] = useState({});
  const [rowSubMappings, setRowSubMappings] = useState({});
  const [rowStaticValues, setRowStaticValues] = useState({});
  const [defaultPublicFromDate, setDefaultPublicFromDate] = useState('');
  const [publishDateInput, setPublishDateInput] = useState('');

  // --- Menu State ---
  const [savedMenus, setSavedMenus] = useState([]);
  const [localMenus, setLocalMenus] = useState([]);
  const [selectedMenuId, setSelectedMenuId] = useState(null);
  const [pendingMenuId, setPendingMenuId] = useState(null);
  const [isMenuDirty, setIsMenuDirty] = useState(false);
  const [isLoadingMenus, setIsLoadingMenus] = useState(true);
  const [copiedMenuTemplate, setCopiedMenuTemplate] = useState(null);

  // --- Derived Flags ---
  const hasData = parsedData.length > 0 || columns.length > 0;
  const isHistoryView = viewMode === 'HISTORY_VIEW';
  const canSave =
    selectedRowIds.length > 0 &&
    !isProcessing &&
    (viewMode === 'DATA_VIEW' || viewMode === 'SETTINGS_VIEW');
  const canUndoHistory = isHistoryView && selectedHistoryIds.length > 0 && !isProcessing;

  const canDeleteData =
    (viewMode === 'SETTINGS_VIEW' && selectedCustomIds.length > 0 && !isProcessing) ||
    (viewMode === 'MENU_VIEW' && selectedMenuId !== null && !isProcessing) ||
    (viewMode === 'DATA_VIEW' && selectedRowIds.length > 0 && !isProcessing) ||
    (viewMode === 'DUPLICATE_CHECK_VIEW' &&
      (selectedRowIds.length > 0 || checkedRightIds.length > 0) &&
      !isProcessing) ||
    (viewMode === 'HISTORY_VIEW' && selectedHistoryIds.length > 0 && !isProcessing);

  // --- Basic Pure UI Handlers ---
  const clearSelections = () => {
    setSelectedRowIds([]);
    setSelectedCustomIds([]);
    setSelectedHistoryIds([]);
    setCheckedRightIds([]);
  };

  const handleNavFirst = () => {
    clearSelections();
    setViewMode('MENU_VIEW');
  };

  const handleNavLast = () => {
    clearSelections();
    setViewMode('HISTORY_VIEW');
  };

  const handleNavBack = () => {
    clearSelections();
    if (viewMode === 'HISTORY_VIEW') setViewMode('SETTINGS_VIEW');
    else if (viewMode === 'SETTINGS_VIEW') setViewMode('DUPLICATE_CHECK_VIEW');
    else if (viewMode === 'DUPLICATE_CHECK_VIEW') setViewMode('DATA_VIEW');
    else if (viewMode === 'DATA_VIEW') setViewMode('MENU_VIEW');
  };

  const handleNavForward = () => {
    clearSelections();
    if (viewMode === 'MENU_VIEW') setViewMode('DATA_VIEW');
    else if (viewMode === 'DATA_VIEW') setViewMode('DUPLICATE_CHECK_VIEW');
    else if (viewMode === 'DUPLICATE_CHECK_VIEW') setViewMode('SETTINGS_VIEW');
    else if (viewMode === 'SETTINGS_VIEW') setViewMode('HISTORY_VIEW');
  };

  const handleAddCustomRow = () => {
    setCustomRows((prev) => [
      ...prev,
      { id: `custom_${Date.now()}`, name: '-', isCustom: true },
    ]);
    setIsMenuDirty(true);
  };

  const handleCustomRowNameChange = (id, newName) => {
    const rowToUpdate = customRows.find((r) => r.id === id);
    const oldName = rowToUpdate ? rowToUpdate.name : null;

    setCustomRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, name: newName } : row)),
    );

    if (oldName && oldName !== '-' && newName !== '-') {
      setColumns((prevCols) => prevCols.map((c) => (c === oldName ? newName : c)));
      setParsedData((prevData) =>
        prevData.map((row) => {
          if (row[oldName] !== undefined) {
            const newRow = { ...row, [newName]: row[oldName] };
            delete newRow[oldName];
            return newRow;
          }
          return row;
        }),
      );
    } else if (newName && newName !== '-') {
      setColumns((prevCols) => {
        const normNew = normalizeString(newName);
        if (!prevCols.some((c) => normalizeString(c) === normNew)) {
          return [...prevCols, newName];
        }
        return prevCols;
      });
    }
    setIsMenuDirty(true);
  };

  const handleSelectAllRows = (e) => {
    if (e.target.checked) {
      const unimported = parsedData.filter(
        (row) => !successfullyImportedIds.includes(row.id),
      );
      setSelectedRowIds(unimported.map((row) => row.id));
    } else {
      setSelectedRowIds([]);
    }
  };

  const handleSelectRow = (id) => {
    if (successfullyImportedIds.includes(id)) return;
    setSelectedRowIds((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id],
    );
  };

  const handleMissingColumnsCancel = () => {
    setMissingColumnsDialog({ open: false, missingCols: [] });
  };

  const handleBulkMatchCancel = () => {
    setBulkMatchState((prev) => ({ ...prev, open: false }));
  };

  const handleCancelProcess = () => {
    cancelProcessRef.current = true;
    setIsCancelling(true);
    setProgressMessage('Cancelling... (finishing current item)');
  };

  return {
    refs: { fileInputRef, cancelProcessRef, isTemplateFromReinstateRef },
    state: {
      viewMode,
      parsedData,
      columns,
      selectedRowIds,
      currentFilename,
      pendingNextColumn,
      userZoomLevel,
      importHistory,
      selectedHistoryIds,
      isLoadingHistory,
      successfullyImportedIds,
      sessionBatches,
      isPhotoFlowActive,
      pendingMissingColsCheck,
      stagedFiles,
      manualMatchRowId,
      resolveCoordsState,
      invalidMatchState,
      bulkMatchState,
      missingColumnsDialog,
      isProcessing,
      progressMessage,
      uploadProgress,
      isCancelling,
      categories,
      regions,
      globalCategory,
      customRows,
      markerIcons,
      makeUppercase,
      rteFontType,
      rteFontSize,
      imHeadingFontType,
      imHeadingFontSize,
      selectedCustomIds,
      rowMappings,
      rowSubMappings,
      rowStaticValues,
      defaultPublicFromDate,
      publishDateInput,
      savedMenus,
      localMenus,
      selectedMenuId,
      pendingMenuId,
      isMenuDirty,
      isLoadingMenus,
      copiedMenuTemplate,
      alertState,
      confirmDialog,
      showHistoryDeleteConfirm,
      showHistoryDeleteConfirm2,
      showPublishConfirm1,
      showBatchFilenameDialog,
      checkedRightIds,
      triggerDuplicateDelete,
      pendingReinstateBatchIds,
      pendingDuplicateDeletions,
      pendingDocumentDeletions,
    },
    setters: {
      setViewMode,
      setParsedData,
      setColumns,
      setSelectedRowIds,
      setCurrentFilename,
      setPendingNextColumn,
      setUserZoomLevel,
      setImportHistory,
      setSelectedHistoryIds,
      setIsLoadingHistory,
      setSuccessfullyImportedIds,
      setSessionBatches,
      setIsPhotoFlowActive,
      setPendingMissingColsCheck,
      setStagedFiles,
      setManualMatchRowId,
      setResolveCoordsState,
      setInvalidMatchState,
      setBulkMatchState,
      setMissingColumnsDialog,
      setIsProcessing,
      setProgressMessage,
      setUploadProgress,
      setIsCancelling,
      setCategories,
      setRegions,
      setGlobalCategory,
      setCustomRows,
      setMarkerIcons,
      setMakeUppercase,
      setRteFontType,
      setRteFontSize,
      setImHeadingFontType,
      setImHeadingFontSize,
      setSelectedCustomIds,
      setRowMappings,
      setRowSubMappings,
      setRowStaticValues,
      setDefaultPublicFromDate,
      setPublishDateInput,
      setSavedMenus,
      setLocalMenus,
      setSelectedMenuId,
      setPendingMenuId,
      setIsMenuDirty,
      setIsLoadingMenus,
      setCopiedMenuTemplate,
      setAlertState,
      setConfirmDialog,
      setShowHistoryDeleteConfirm,
      setShowHistoryDeleteConfirm2,
      setShowPublishConfirm1,
      setShowBatchFilenameDialog,
      setCheckedRightIds,
      setTriggerDuplicateDelete,
      setPendingReinstateBatchIds,
      setPendingDuplicateDeletions,
      setPendingDocumentDeletions,
    },
    flags: { hasData, isHistoryView, canSave, canDeleteData, canUndoHistory },
    uiActions: {
      handleNavFirst,
      handleNavLast,
      handleNavBack,
      handleNavForward,
      handleAddCustomRow,
      handleCustomRowNameChange,
      handleSelectAllRows,
      handleSelectRow,
      handleMissingColumnsCancel,
      handleBulkMatchCancel,
      handleCancelProcess,
    },
  };
};
